import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import type { TemplateInformation } from "../types/api-types";
import useTemplateFilters from "./hooks/use-template-filters";
import TemplateSearchFilters from "./components/template-search-filters";
import TemplateTableRow from "./components/template-table-row";
import ExportCsvButton from "../../shared/export/export-csv-button";
import type { CsvColumn } from "../../shared/export/csv-export";

const yesNo = (value: boolean) => (value ? "Yes" : "No");

// The table only shows the name and validator flag, so the export also
// includes each template's configuration to make the file useful on its own.
const exportColumns: CsvColumn<TemplateInformation>[] = [
  { header: "Template Name", value: (t) => t.description },
  { header: "Data Type", value: (t) => t.dataType?.description },
  { header: "Min Length", value: (t) => t.minLength },
  { header: "Max Length", value: (t) => t.maxLength },
  { header: "Allow Letters", value: (t) => yesNo(t.allowLetters) },
  { header: "Allow Numbers", value: (t) => yesNo(t.allowNumbers) },
  { header: "Allow Spaces", value: (t) => yesNo(t.allowSpaces) },
  { header: "Allow Future Dates", value: (t) => yesNo(t.allowFutureDates) },
  {
    header: "Used in Data Validator",
    value: (t) => yesNo(t.useDataValidator),
  },
];

interface TemplateTableProps {
  templates: TemplateInformation[];
  onTemplateSelect: (template: TemplateInformation) => void;
  onToggleValidator: (template: TemplateInformation, checked: boolean) => void;
  isSaving?: boolean;
}

const TemplateTable = ({
  templates,
  onTemplateSelect,
  onToggleValidator,
  isSaving = false,
}: TemplateTableProps) => {
  const {
    searchTerm,
    setSearchTerm,
    validatorFilter,
    setValidatorFilter,
    filteredTemplates,
  } = useTemplateFilters(templates);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 600px)",
        minHeight: 400,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Template Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click on a template row to configure its fields, data types, and
            validation rules.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={`${filteredTemplates.length} ${filteredTemplates.length === 1 ? "Template" : "Templates"}`}
            color="primary"
            variant="outlined"
          />
          <ExportCsvButton
            rows={filteredTemplates}
            columns={exportColumns}
            fileName="templates"
          />
        </Box>
      </Box>

      <TemplateSearchFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        validatorFilter={validatorFilter}
        onValidatorFilterChange={setValidatorFilter}
      />

      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: (theme) => theme.palette.action.hover,
              }}
            >
              <TableCell
                sx={{
                  backgroundColor: (theme) => theme.palette.grey[50],
                  borderBottom: 2,
                  borderColor: "divider",
                  py: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="text.primary"
                >
                  Template Name
                </Typography>
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  width: 250,
                  backgroundColor: (theme) => theme.palette.grey[50],
                  borderBottom: 2,
                  borderColor: "divider",
                  py: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="text.primary"
                >
                  Used in Data Validator
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
        </Table>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              display: "none",
              width: 0,
              height: 0,
            },
            scrollbarWidth: "none",
            "-ms-overflow-style": "none",
          }}
        >
          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableBody>
              {filteredTemplates.map((template) => (
                <TemplateTableRow
                  key={template.id}
                  template={template}
                  onSelect={onTemplateSelect}
                  onToggleValidator={onToggleValidator}
                  isSaving={isSaving}
                />
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
};

export default TemplateTable;
